// hooks.js - Custom hooks for auth operations
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  login,
  logoutUser,
  setEmail,
  setPassword,
  refreshTokenIfNeeded,
  AUTH_STATE,
} from ".";

/**
 * Hook for auth-related operations and state
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  // Login handler
  const handleLogin = useCallback(
    (username, password) => {
      return dispatch(login({ username, password }));
    },
    [dispatch]
  );

  // Logout handler
  const handleLogout = useCallback(() => {
    return dispatch(logoutUser());
  }, [dispatch]);

  // Form input handlers
  const handleEmailChange = useCallback(
    (email) => {
      dispatch(setEmail(email));
    },
    [dispatch]
  );

  const handlePasswordChange = useCallback(
    (password) => {
      dispatch(setPassword(password));
    },
    [dispatch]
  );

  // Token refresh
  const checkAndRefreshToken = useCallback(() => {
    return dispatch(refreshTokenIfNeeded());
  }, [dispatch]);

  // Auth state derived properties
  const isAuthenticated = auth.state === AUTH_STATE.AUTHENTICATED;
  const isLoading = auth.isRefreshing || auth.isUserLoading;
  const isLoaded = !auth.isRefreshing && !auth.isUserLoading;
  const hasLicenseError = auth.state === AUTH_STATE.FAILED_LICENSE;
  const hasAuthError = auth.state === AUTH_STATE.FAILED;

  return {
    // State
    user: auth.user,
    email: auth.email,
    password: auth.password,
    accessCode: auth.accessCode,
    isAuthenticated,
    isLoading,
    isLoaded,
    hasLicenseError,
    hasAuthError,

    // Actions
    login: handleLogin,
    logout: handleLogout,
    setEmail: handleEmailChange,
    setPassword: handlePasswordChange,
    refreshToken: checkAndRefreshToken,
  };
};
